import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useCallback, useMemo } from "react"

import {
  ENVIRONMENT_NAME_DEVELOPMENT,
  ENVIRONMENT_NAME_PRODUCTION,
  ENVIRONMENT_NAME_SANDBOX,
  type Environment,
  getEnvironments,
} from "@/api/environment"
import { queryKeys } from "@/queries/keys"

// Environments are seeded into the query cache from bootstrap viewData by
// `hydrateQueriesFromViewData`. They ship inline (never deferred) and change
// rarely, so we treat the hydrated data as authoritative and let mutations keep
// the cache current instead of refetching on every mount. The queryFn is the
// fallback for render paths that mount without hydrated data (e.g. a direct
// route load), where it fetches the org's environments once.
const EMPTY_ENVIRONMENTS: readonly Environment[] = []

function useEnvironmentsQuery() {
  return useQuery({
    queryKey: queryKeys.environments.all,
    async queryFn() {
      const { data: environments } = await getEnvironments()
      return environments
    },
    staleTime: Infinity,
  })
}

export interface EnvironmentsQueryState {
  environments: readonly Environment[]
  isReady: boolean
  isLoading: boolean
  isError: boolean
  retry: () => void
}

export function useEnvironmentsQueryState(): EnvironmentsQueryState {
  const query = useEnvironmentsQuery()
  const { refetch } = query
  const retry = useCallback(() => {
    void refetch()
  }, [refetch])

  return {
    environments: query.data ?? EMPTY_ENVIRONMENTS,
    isReady: query.data !== undefined,
    isLoading: query.isPending || query.isFetching,
    // While a retry is in flight React Query keeps `isError` raised, so let
    // consumers render their loading state instead of pinning the error UI.
    isError: query.isError && !query.isFetching,
    retry,
  }
}

export function useEnvironmentsReady(): boolean {
  return useEnvironmentsQuery().data !== undefined
}

function useEnvironmentsData(): readonly Environment[] {
  const { data } = useEnvironmentsQuery()
  return data ?? EMPTY_ENVIRONMENTS
}

export function useAllEnvironments(options?: {
  includeSandboxEnvironment?: boolean
}): Environment[] {
  const environments = useEnvironmentsData()
  // Memoized because callers pass the result into effect dependency arrays;
  // filter/sort always allocate, so a fresh array every render would loop.
  return useMemo(() => {
    const envs = options?.includeSandboxEnvironment
      ? [...environments]
      : environments.filter((e) => e.name !== ENVIRONMENT_NAME_SANDBOX)
    // Sort environments by putting the production environment first.
    envs.sort((a, b) => {
      if (a.name === ENVIRONMENT_NAME_PRODUCTION) {
        return -1
      }
      if (b.name === ENVIRONMENT_NAME_PRODUCTION) {
        return 1
      }
      return 0
    })
    return envs
  }, [environments, options?.includeSandboxEnvironment])
}

export const useEnvironmentById = (id?: number): Environment | undefined => {
  const environments = useEnvironmentsData()
  return id !== undefined ? environments.find((e) => e.id === id) : undefined
}

export const useProdEnvironment = (): Environment | undefined => {
  const allEnvironments = useAllEnvironments()
  return allEnvironments.find((e) => e.name === ENVIRONMENT_NAME_PRODUCTION)
}

export const useSandboxEnvironment = (): Environment | undefined => {
  const environments = useEnvironmentsData()
  return environments.find((e) => e.name === ENVIRONMENT_NAME_SANDBOX)
}

export const useDevelopmentEnvironment = (): Environment | undefined => {
  const environments = useEnvironmentsData()
  return environments.find((e) => e.name === ENVIRONMENT_NAME_DEVELOPMENT)
}

export const useNonProdEnvironments = (options?: {
  includeSandboxEnvironment?: boolean
}): Environment[] => {
  const allEnvironments = useAllEnvironments(options)
  return useMemo(
    () => allEnvironments.filter((e) => e.name !== ENVIRONMENT_NAME_PRODUCTION),
    [allEnvironments],
  )
}

/**
 * Cache writers for environment mutations. Replaces the old Redux
 * create/update/delete dispatches — mutation call sites update the query cache
 * directly so the shared `environments` query stays current without a refetch.
 */
export function useEnvironmentCache() {
  const queryClient = useQueryClient()

  const upsertEnvironment = (environment: Environment) => {
    queryClient.setQueryData<Environment[]>(queryKeys.environments.all, (previous) => {
      const environments = previous ?? []
      const index = environments.findIndex((e) => e.id === environment.id)
      if (index === -1) {
        return [...environments, environment]
      }
      const next = environments.slice()
      next[index] = environment
      return next
    })
  }

  const removeEnvironment = (id: number) => {
    queryClient.setQueryData<Environment[]>(queryKeys.environments.all, (previous) =>
      (previous ?? []).filter((e) => e.id !== id),
    )
  }

  return { upsertEnvironment, removeEnvironment }
}
